"use client"

import { useState, useEffect } from "react"
import { FileText, Search, Edit, Eye, CheckCircle, Clock, Download, Bell, ExternalLink, Mail, ShieldCheck, User, MoreHorizontal } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "react-toastify"
import MasterSideBar from "../../components/MasterSideBar.jsx"
import TopbarNav from "../../components/TopbarNav"
import { getVendors, getVendorById, updateVendorContract } from "../../api/vendorApi"
import VendorProfileModal from "../../components/operationsAssociate/VendorProfileModal"
import ContractActionModal from "../../components/operationsAssociate/ContractActionModal"

const ContractManagementSystemHO = () => {
  const [contracts, setContracts] = useState([])
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedVendorId, setSelectedVendorId] = useState(null)
  const [showVendorModal, setShowVendorModal] = useState(false)
  const [showActionModal, setShowActionModal] = useState(false)
  const [actionModalMode, setActionModalMode] = useState("edit")
  const [actionContract, setActionContract] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    vendor: "",
    type: "",
  })

  const calculateProgress = (startDate, endDate, status) => {
    if (status === "expired") return 100
    if (status === "draft") return 0
    const start = new Date(startDate)
    const end = new Date(endDate)
    const now = new Date()
    if (now < start) return 0
    if (now > end) return 100
    const totalDays = (end - start) / (1000 * 60 * 60 * 24)
    const elapsedDays = (now - start) / (1000 * 60 * 60 * 24)
    return Math.round((elapsedDays / totalDays) * 100)
  }

  const determineStatus = (startDate, endDate, currentStatus) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const now = new Date()
    const daysUntilExpiry = Math.ceil((end - now) / (1000 * 60 * 60 * 24))
    if (now > end) return "expired"
    if (daysUntilExpiry <= 30 && daysUntilExpiry > 0) return "expiring_soon"
    if (now >= start && now <= end) return "active"
    if (now < start) return "pending"
    return currentStatus || "draft"
  }

  const fetchContractsAndVendors = async () => {
    setLoading(true)
    setError(null)
    try {
      const vendorsResponse = await getVendors()
      const allVendors = vendorsResponse.data.data
      setVendors(allVendors)

      const contractPromises = allVendors.map((vendor) =>
        getVendorById(vendor._id).then((res) => ({
          vendor: res.data.data,
          contracts: res.data.data.contracts,
        })),
      )

      const contractsWithVendors = await Promise.all(contractPromises)
      const allContracts = contractsWithVendors.flatMap(
        (item) =>
          item.contracts?.map((contract) => ({
            ...contract,
            vendor: item.vendor,
            status: determineStatus(contract.startDate, contract.endDate, contract.status),
            completionPercentage: calculateProgress(contract.startDate, contract.endDate, contract.status),
          })) || [],
      )

      const filteredResults = allContracts.filter((contract) => {
        const searchTerm = filters.search.toLowerCase()
        const vendorName = contract.vendor?.name?.toLowerCase() || ""
        const matchesSearch =
          contract.title?.toLowerCase().includes(searchTerm) ||
          vendorName.includes(searchTerm) ||
          contract.contractNumber?.toLowerCase().includes(searchTerm)

        const matchesStatus = filters.status === "" || contract.status === filters.status
        const matchesVendor = filters.vendor === "" || (contract.vendor && contract.vendor._id === filters.vendor)
        const matchesType = filters.type === "" || contract.type === filters.type
        return matchesSearch && matchesStatus && matchesVendor && matchesType
      })

      setContracts(filteredResults)
    } catch (err) {
      console.error("Failed to fetch data:", err)
      setError("Synchronization failed with Contract Registry.")
      toast.error("Failed to load contracts or vendors.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchContractsAndVendors()
  }, [filters])

  const handleManageContract = (contract) => {
    setActionContract(contract)
    setActionModalMode("edit")
    setShowActionModal(true)
  }

  const handleSaveEdit = async (updatedContract) => {
    try {
      const { vendor, ...cleanContract } = updatedContract
      await updateVendorContract(vendor._id, cleanContract._id, cleanContract)
      toast.success("Contract updated successfully! 🎉")
      fetchContractsAndVendors()
      setShowActionModal(false)
    } catch (err) {
      console.error("Edit failure:", err)
      toast.error(err.response?.data?.message || "Protocol update failed.")
    }
  }

  const handleSaveRenewal = async (renewalData) => {
    try {
      const updatedContract = {
        ...actionContract,
        endDate: renewalData.newEndDate,
        value: renewalData.newValue,
        status: "active", // Renewal typically resets status to active
        // Any other fields from renewalData
      }
      const { vendor, ...cleanContract } = updatedContract
      await updateVendorContract(actionContract.vendor._id, actionContract._id, cleanContract)
      toast.success("Engagement period expanded! 📄")
      fetchContractsAndVendors()
      setShowActionModal(false)
    } catch (err) {
      console.error("Renewal failure:", err)
      toast.error(err.response?.data?.message || "Protocol expansion failed.")
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-emerald-50 text-emerald-600"
      case "pending": return "bg-amber-50 text-amber-600"
      case "expired": return "bg-red-50 text-red-600"
      case "expiring_soon": return "bg-orange-50 text-orange-600"
      default: return "bg-gray-50 text-gray-600"
    }
  }

  const ContractStats = () => {
    const stats = {
      total: contracts.length,
      active: contracts.filter(c => c.status === 'active').length,
      expiring: contracts.filter(c => c.status === 'expiring_soon').length,
      value: contracts.reduce((sum, c) => sum + (c.value || 0), 0)
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-blue-500 hover:shadow-lg transition-all">
          <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Contracts</h3>
          <p className="text-3xl font-black text-gray-800 mt-2">{stats.total}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-emerald-500 hover:shadow-lg transition-all">
          <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Active Agreements</h3>
          <p className="text-3xl font-black text-gray-800 mt-2">{stats.active}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-orange-500 hover:shadow-lg transition-all">
          <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Expiring Soon</h3>
          <p className="text-3xl font-black text-gray-800 mt-2">{stats.expiring}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-purple-500 hover:shadow-lg transition-all">
          <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Value</h3>
          <p className="text-3xl font-black text-gray-800 mt-2">${(stats.value / 1000).toFixed(1)}K</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <MasterSideBar 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />
      
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} />
        
        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto space-y-8">
            {/* Header Action Bar */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase">
                  Contract <span className="text-orange-600">Registry</span>
                </h1>
                <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mt-1">Status: Lifecycle Management Active</p>
              </div>
            </div>

            {/* Stats Dashboard */}
            <ContractStats />

            {/* Filter Control Center */}
            <section className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                <div className="md:col-span-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="SEARCH CONTRACTS..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl text-[10px] font-black focus:ring-2 focus:ring-orange-500 uppercase tracking-widest"
                  />
                </div>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="p-3 bg-gray-50 border-none rounded-xl text-[10px] font-black text-gray-600 uppercase tracking-widest focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="expired">Expired</option>
                  <option value="expiring_soon">Expiring Soon</option>
                </select>
                <select
                  value={filters.vendor}
                  onChange={(e) => setFilters({ ...filters, vendor: e.target.value })}
                  className="p-3 bg-gray-50 border-none rounded-xl text-[10px] font-black text-gray-600 uppercase tracking-widest focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">All Vendors</option>
                  {vendors.map((vendor) => (
                    <option key={vendor._id} value={vendor._id}>{vendor.name}</option>
                  ))}
                </select>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  className="p-3 bg-gray-50 border-none rounded-xl text-[10px] font-black text-gray-600 uppercase tracking-widest focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">All Types</option>
                  <option value="service">Service</option>
                  <option value="licensing">Licensing</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="consulting">Consulting</option>
                </select>
              </div>
            </section>

            {/* Data Grid Table */}
            <section className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Contract Profile</th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Vendor Relationship</th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Financials</th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status & Risk</th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Timeline</th>
                      <th className="px-8 py-5 text-right"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {loading ? (
                      <tr><td colSpan="6" className="px-8 py-10 text-center"><div className="flex justify-center"><span className="animate-spin h-6 w-6 border-2 rounded-full border-orange-500 border-t-transparent"></span></div></td></tr>
                    ) : contracts.length === 0 ? (
                      <tr><td colSpan="6" className="px-8 py-20 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest italic">No Registry Records Found</td></tr>
                    ) : (
                      contracts.map((contract) => (
                        <tr key={contract._id} className="hover:bg-gray-50/50 transition-colors group">
                          <td className="px-8 py-5">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-black text-xs uppercase">
                                {contract.title?.substring(0, 2) || "CT"}
                              </div>
                              <div>
                                <p className="text-sm font-black text-gray-800 uppercase leading-none">{contract.title}</p>
                                <p className="text-[9px] font-black text-orange-600 mt-1 uppercase tracking-tighter italic">{contract.contractNumber}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-8 py-5">
                            <p className="text-xs font-bold text-gray-700">{contract.vendor?.name}</p>
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{contract.type}</p>
                          </td>
                          <td className="px-8 py-5">
                            <p className="text-sm font-black text-gray-800">${(contract.value || 0).toLocaleString()}</p>
                          </td>
                          <td className="px-8 py-5">
                            <div className="space-y-1">
                              <span className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-tighter ${getStatusColor(contract.status)}`}>
                                {contract.status?.replace("_", " ")}
                              </span>
                              <p className={`text-[8px] font-black uppercase tracking-widest ${contract.riskLevel === 'high' ? 'text-red-500' : contract.riskLevel === 'medium' ? 'text-orange-500' : 'text-emerald-500'}`}>
                                Risk: {contract.riskLevel}
                              </p>
                            </div>
                          </td>
                          <td className="px-8 py-5">
                            <div className="w-24 bg-gray-100 rounded-full h-1 mb-1">
                              <div 
                                className={`h-full rounded-full transition-all duration-700 ${contract.completionPercentage >= 90 ? "bg-red-500" : contract.completionPercentage >= 75 ? "bg-orange-500" : "bg-emerald-500"}`}
                                style={{ width: `${contract.completionPercentage}%` }}
                              />
                            </div>
                            <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">
                              {new Date(contract.startDate).toLocaleDateString()} - {new Date(contract.endDate).toLocaleDateString()}
                            </p>
                          </td>
                          <td className="px-8 py-5 text-right">
                            <button
                              onClick={() => handleManageContract(contract)}
                              className="flex items-center gap-2 ml-auto bg-gray-900 text-white px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all opacity-0 group-hover:opacity-100 shadow-lg shadow-gray-200"
                            >
                              Manage <ExternalLink size={12}/>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showActionModal && actionContract && (
          <ContractActionModal
            isOpen={showActionModal}
            onClose={() => {
              setShowActionModal(false)
              setActionContract(null)
            }}
            contract={actionContract}
            mode={actionModalMode}
            onSave={handleSaveEdit}
            onRenew={handleSaveRenewal}
          />
        )}

        {showVendorModal && selectedVendorId && (
          <VendorProfileModal
            vendorId={selectedVendorId}
            onClose={() => {
              setShowVendorModal(false)
              setSelectedVendorId(null)
            }}
            onUpdate={() => fetchContractsAndVendors()}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

export default ContractManagementSystemHO
